import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useHistory } from '../hooks/useHistory';
import { useAlert } from '../hooks/useAlert';
import Header from '../components/Header';
import CustomAlert from '../components/CustomAlert';
import './TranscriptHistory.css';

function TranscriptHistory() {
  const { transcriptId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const { getVersionHistory, getTranscript, restoreVersion, createTranscriptFromVersion, isLoading, error } = useHistory();
  const navigate = useNavigate();
  const { alert, showConfirm, showSuccess, showError } = useAlert();

  const [transcript, setTranscript] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    if (transcriptId) {
      loadTranscriptAndHistory();
    }
  }, [isAuthenticated, transcriptId, navigate]);

  const loadTranscriptAndHistory = async () => {
    try {
      const [transcriptData, versionsData] = await Promise.all([
        getTranscript(transcriptId),
        getVersionHistory(transcriptId)
      ]);

      setTranscript(transcriptData);
      setVersions(versionsData);
    } catch (err) {
      console.error('Error loading transcript history:', err);
      showError('Error', `Error loading transcript history: ${err.message}`);
      navigate('/dashboard');
    }
  };

  const handleRestoreVersion = async (versionId) => {
    const confirmed = await showConfirm(
      'Restore Version',
      'Are you sure you want to restore this version? This will create a new transcript with the restored content.',
      { confirmText: 'Restore', cancelText: 'Cancel' }
    );

    if (!confirmed) {
      return;
    }

    setIsRestoring(true);
    try {
      const newTranscript = await createTranscriptFromVersion(transcriptId, versionId);
      showSuccess('Success', 'New transcript created from version successfully!');
      navigate(`/?transcript=${newTranscript.id}`);
    } catch (err) {
      showError('Restore Failed', `Failed to create transcript from version: ${err.message}`);
    } finally {
      setIsRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVersionDescription = (version) => {
    if (version.type === 'original') return 'Original transcription';
    if (version.type === 'edited') return 'Manual edits';
    if (version.type === 'corrected') return 'AI corrections';
    if (version.type === 'restored') return `Restored from version ${version.restoredFromVersion}`;
    return 'Modified';
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <Header />
        <h1>Error</h1>
        <main className="error-main">
          <p>Error loading transcript history: {error}</p>
          <Link to="/dashboard" className="back-button">← Back to Dashboard</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="transcript-history-page">
      <Header />
      <div className="page-title">
        <h1>Version History</h1>
        {transcript && (
          <p>Viewing versions for: <strong>{transcript.name}</strong></p>
        )}
      </div>

      <main className="history-main">
        <div className="history-container">
          <div className="history-header">
            <Link to="/dashboard" className="back-button">← Back to Dashboard</Link>
            {transcript && (
              <Link
                to={`/?transcript=${transcript.id}`}
                className="edit-button"
              >
                Edit Current Version
              </Link>
            )}
          </div>

          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading version history...</p>
            </div>
          ) : versions.length === 0 ? (
            <div className="empty-state">
              <h3>No Version History</h3>
              <p>This transcript has no saved versions yet.</p>
            </div>
          ) : (
            <div className="versions-list">
              {versions.map((version, index) => (
                <div key={version.id} className="version-card">
                  <div className="version-header">
                    <div className="version-info">
                      <h3>Version {versions.length - index}</h3>
                      <span className="version-type">{getVersionDescription(version)}</span>
                    </div>
                    <div className="version-meta">
                      <span className="version-date">{formatDate(version.createdAt)}</span>
                      {index > 0 && (
                        <button
                          onClick={() => handleRestoreVersion(version.id)}
                          disabled={isRestoring}
                          className="restore-button"
                        >
                          {isRestoring ? 'Restoring...' : 'Restore'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="version-preview">
                    <button
                      onClick={() => setSelectedVersion(selectedVersion === version.id ? null : version.id)}
                      className="toggle-preview"
                    >
                      {selectedVersion === version.id ? 'Hide Preview' : 'Show Preview'}
                    </button>

                    {selectedVersion === version.id && (
                      <div className="version-content">
                        <pre>{version.data?.text?.substring(0, 500) || 'No content available'}</pre>
                        {version.data?.text?.length > 500 && <p>... (truncated)</p>}
                      </div>
                    )}
                  </div>

                  {version.changes && (
                    <div className="version-changes">
                      <h4>Changes:</h4>
                      <ul>
                        {Array.isArray(version.changes)
                          ? version.changes.map((change, idx) => (
                              <li key={idx}>{change}</li>
                            ))
                          : (
                              <li>
                                {typeof version.changes === 'string'
                                  ? version.changes
                                  : JSON.stringify(version.changes)
                                }
                              </li>
                            )
                        }
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <CustomAlert
        isOpen={alert?.isOpen}
        onClose={alert?.onClose}
        onConfirm={alert?.onConfirm}
        title={alert?.title}
        message={alert?.message}
        type={alert?.type}
        confirmText={alert?.confirmText}
        cancelText={alert?.cancelText}
        showCancel={alert?.showCancel}
      />
    </div>
  );
}

export default TranscriptHistory;